import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Send, Bot, User, Fingerprint, Briefcase, FileText, Shield, CreditCard, 
  Users, ChevronRight, Loader2, BookOpen, Truck, Upload, Star, 
  HelpCircle, Lock, MapPin, Search, ArrowRight, MessageCircle, X,
  Clipboard, Eye, Settings, PlusCircle, Trash2, Award, BarChart3
} from 'lucide-react';

const GUIDE_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'New to StraightConnect? Start here.',
    icon: BookOpen,
    color: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    accent: 'text-blue-600',
    topics: [
      { label: 'What is StraightConnect?', q: 'What is StraightConnect and what does it do? Give a clear overview of the platform for a brand new user.' },
      { label: 'How do I sign up?', q: 'Walk me through the signup process step by step. What role should I choose - Driver, Carrier Owner, or Dispatcher?' },
      { label: 'Understanding your dashboard', q: 'Explain the dashboard features I see after logging in. What does each section mean?' },
      { label: 'Complete your profile', q: 'What information should I add to my profile to get the most out of StraightConnect? Why does profile completeness matter?' },
    ]
  },
  {
    id: 'verification',
    title: 'Identity Verification',
    description: 'Get verified through Persona to unlock full access.',
    icon: Fingerprint,
    color: 'from-indigo-500 to-violet-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    accent: 'text-indigo-600',
    topics: [
      { label: 'Why do I need to verify?', q: 'Why does StraightConnect require identity verification through Persona? What happens if I skip it?' },
      { label: 'How Persona verification works', q: 'Walk me through the Persona identity verification process step by step. What documents do I need?' },
      { label: 'Verification is stuck or failed', q: 'My Persona verification is stuck on pending or was declined. What should I do to fix it?' },
      { label: 'Admin approval process', q: 'After I verify, does an admin need to approve me? How long does the approval process take?' },
    ]
  },
  {
    id: 'jobs',
    title: 'Job Board',
    description: 'Post, find, and apply for freight opportunities.',
    icon: Briefcase,
    color: 'from-emerald-500 to-green-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    accent: 'text-emerald-600',
    topics: [
      { label: 'How to apply for jobs (Driver)', q: 'I\'m a Driver. How do I find and apply for jobs on StraightConnect? Walk me through the steps.' },
      { label: 'How to post a job (Carrier)', q: 'I\'m a Carrier Owner. How do I post a new job listing? What information do I need to include? How do I edit or remove a job I posted?' },
      { label: 'Manage applications', q: 'How do I review applications from drivers? How do I accept or reject applicants for my posted jobs?' },
      { label: 'AI job matching', q: 'How does AI-powered job matching work? How can I improve my match scores?' },
      { label: 'External load boards', q: 'What external load boards does StraightConnect recommend? Explain DAT, Truckstop, Amazon Relay, and others.' },
    ]
  },
  {
    id: 'documents',
    title: 'Document Uploads',
    description: 'Upload and manage your compliance credentials.',
    icon: Upload,
    color: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    accent: 'text-amber-600',
    topics: [
      { label: 'What documents can I upload?', q: 'What types of documents can I upload on StraightConnect? Explain TWIC cards, Hazmat certifications, DOT Physical cards, CDL licenses, TSA PreCheck, and insurance certificates.' },
      { label: 'How to upload a document', q: 'Walk me through the step-by-step process to upload a compliance document like a TWIC card or Hazmat certificate.' },
      { label: 'Document verification (RMIS)', q: 'How does RMIS and Highway credential verification work? What happens after I upload a document?' },
      { label: 'Truck photo upload (Drivers)', q: 'How do I upload a photo of my truck? Where will it appear on my profile?' },
    ]
  },
  {
    id: 'privacy',
    title: 'Privacy & Security',
    description: 'How we protect your data and your rights.',
    icon: Shield,
    color: 'from-slate-600 to-slate-700',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-slate-600',
    topics: [
      { label: 'Privacy policy summary', q: 'Summarize the StraightConnect privacy policy in simple terms. What data do you collect, how is it used, and who can see it?' },
      { label: 'How is my data protected?', q: 'How does StraightConnect protect my personal data and documents? What encryption and security measures are in place?' },
      { label: 'Delete my data', q: 'How can I request deletion of my account and all personal data from StraightConnect?' },
      { label: 'Account deactivation', q: 'How do I deactivate my StraightConnect account? Is it reversible? What happens to my data afterward?' },
    ]
  },
  {
    id: 'subscriptions',
    title: 'Subscriptions & Billing',
    description: 'Plans, pricing, and payment management.',
    icon: CreditCard,
    color: 'from-purple-500 to-fuchsia-500',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    accent: 'text-purple-600',
    topics: [
      { label: 'What\'s included in Free?', q: 'What features are included in the Free tier of StraightConnect? What are the limitations compared to paid plans?' },
      { label: 'Compare all plans', q: 'Compare all StraightConnect subscription tiers side by side: Free ($0), Starter ($29/mo), Professional ($79/mo), and Enterprise ($199/mo). What does each include?' },
      { label: 'How to upgrade', q: 'How do I upgrade my StraightConnect subscription plan? Walk me through the process step by step.' },
      { label: 'Billing & payments', q: 'How does billing work? When am I charged? Can I cancel anytime? How do refunds work?' },
    ]
  },
  {
    id: 'community',
    title: 'Community & Ratings',
    description: 'Connect, rate, and refer other members.',
    icon: Users,
    color: 'from-rose-500 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    accent: 'text-rose-600',
    topics: [
      { label: 'Community feed', q: 'How does the community feed work? How do I create posts, comment, and interact with other members?' },
      { label: 'Rating system (0-100)', q: 'How does the 0-100 rating system work? How are scores calculated? Can I respond to ratings I receive?' },
      { label: 'Referral program', q: 'How does the referral program work? Where do I find my referral code? What rewards can I earn from successful referrals?' },
      { label: 'Member directory', q: 'How do I use the member directory to find other carriers, drivers, and dispatchers on the platform?' },
    ]
  },
];

const HelpCenter = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (chatOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chatOpen]);

  const sendMessage = async (question) => {
    const q = question || input.trim();
    if (!q || loading) return;

    setChatOpen(true);
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setInput('');
    setLoading(true);

    try {
      const res = await api.post('/help/ask', { question: q });
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.answer }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, I couldn\'t process that. Please try again or email telly.arnold@tlagroupllc.com for support.' }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const filteredSections = searchQuery
    ? GUIDE_SECTIONS.map(section => ({
        ...section,
        topics: section.topics.filter(t =>
          t.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          section.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(s => s.topics.length > 0)
    : GUIDE_SECTIONS;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <div className="relative overflow-hidden bg-slate-900">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur rounded-full text-sm text-slate-300 mb-5 border border-white/10">
                <Bot className="h-4 w-4" />
                AI-Powered Help
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" data-testid="help-center-title">
                How can we help you?
              </h1>
              <p className="text-slate-400 max-w-xl mx-auto mb-8 text-base sm:text-lg">
                Browse guides below or ask our AI assistant anything about StraightConnect.
              </p>

              {/* Search Bar */}
              <div className="max-w-lg mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search topics... (e.g. upload document, privacy, jobs)"
                  className="w-full pl-12 pr-4 py-3.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  data-testid="help-search-input"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Guide Sections */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="space-y-4">
            {filteredSections.map((section, idx) => {
              const Icon = section.icon;
              const isOpen = activeSection === section.id;

              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className={`border rounded-xl overflow-hidden transition-all ${isOpen ? section.border + ' shadow-md' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`guide-section-${section.id}`}
                >
                  {/* Section Header */}
                  <button
                    onClick={() => setActiveSection(isOpen ? null : section.id)}
                    className={`w-full flex items-center gap-4 p-5 text-left transition-colors ${isOpen ? section.bg : 'bg-white hover:bg-slate-50'}`}
                    data-testid={`guide-toggle-${section.id}`}
                  >
                    <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold text-slate-900 text-base">{section.title}</h2>
                      <p className="text-sm text-slate-500 mt-0.5">{section.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{section.topics.length}</span>
                      <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
                    </div>
                  </button>

                  {/* Topics List */}
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-4 pt-1 space-y-1.5">
                          {section.topics.map((topic) => (
                            <button
                              key={topic.label}
                              onClick={() => sendMessage(topic.q)}
                              className="w-full flex items-center gap-3 p-3 rounded-lg text-left hover:bg-white transition-colors group border border-transparent hover:border-slate-200 hover:shadow-sm"
                              data-testid={`topic-btn-${topic.label.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}
                            >
                              <MessageCircle className={`h-4 w-4 flex-shrink-0 text-slate-300 group-hover:${section.accent}`} />
                              <span className="text-sm text-slate-700 group-hover:text-slate-900 font-medium flex-1">{topic.label}</span>
                              <ArrowRight className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {searchQuery && filteredSections.length === 0 && (
            <div className="text-center py-16">
              <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No topics match "{searchQuery}"</p>
              <p className="text-slate-400 text-sm mt-1">Try a different search or ask the AI assistant directly</p>
              <button
                onClick={() => { setSearchQuery(''); setChatOpen(true); }}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Bot className="h-4 w-4" />
                Ask AI Assistant
              </button>
            </div>
          )}

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-10 text-center"
          >
            <div className="inline-flex flex-col items-center gap-3 p-6 bg-white border border-slate-200 rounded-2xl shadow-sm">
              <p className="text-slate-600 text-sm">Can't find what you're looking for?</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={() => setChatOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800 transition-colors"
                  data-testid="open-ai-chat-btn"
                >
                  <Bot className="h-4 w-4" />
                  Ask AI Assistant
                </button>
                <a
                  href="mailto:telly.arnold@tlagroupllc.com"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  Email Support
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Floating Chat Panel */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 w-[calc(100vw-2rem)] sm:w-[420px] max-h-[70vh] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden"
            data-testid="chat-panel"
          >
            {/* Chat Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold">AI Assistant</p>
                  <p className="text-[11px] text-slate-400">Powered by Claude</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <button
                    onClick={() => setMessages([])}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                    title="Clear chat"
                    data-testid="chat-clear-btn"
                  >
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </button>
                )}
                <button
                  onClick={() => setChatOpen(false)}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="chat-close-btn"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[50vh]">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                    <Bot className="h-6 w-6 text-slate-300" />
                  </div>
                  <p className="text-slate-500 text-sm font-medium">Ask me anything</p>
                  <p className="text-slate-400 text-xs mt-1 max-w-[240px]">Privacy, jobs, documents, subscriptions, verification, and more</p>
                </div>
              )}
              {messages.map((msg, i) => (
                <motion.div
                  key={`${msg.role}-${i}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="h-3.5 w-3.5 text-white" />
                    </div>
                  )}
                  <div className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-slate-900 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md'
                  }`}>
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="h-3.5 w-3.5 text-slate-500" />
                    </div>
                  )}
                </motion.div>
              ))}
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="bg-slate-100 rounded-2xl rounded-bl-md px-3.5 py-2.5 flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin" />
                    <span className="text-xs text-slate-400">Thinking...</span>
                  </div>
                </motion.div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat Input */}
            <div className="border-t border-slate-100 p-3 flex-shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Type your question..."
                  disabled={loading}
                  className="flex-1 px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 bg-slate-50"
                  data-testid="help-input"
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={loading || !input.trim()}
                  className="p-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-colors disabled:opacity-30"
                  data-testid="help-send-btn"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Chat Toggle (when panel is closed) */}
      {!chatOpen && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          onClick={() => setChatOpen(true)}
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 w-14 h-14 bg-slate-900 text-white rounded-2xl shadow-lg hover:bg-slate-800 transition-colors flex items-center justify-center z-50 group"
          data-testid="chat-fab"
        >
          <MessageCircle className="h-6 w-6 group-hover:scale-110 transition-transform" />
          {messages.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {messages.filter(m => m.role === 'assistant').length}
            </span>
          )}
        </motion.button>
      )}

      <Footer />
    </div>
  );
};

export default HelpCenter;
